import {
  type BoundingBoxSliderControl,
  type LineStyle,
} from '@wandb/weave/common/components/MediaCard';
import {colorFromName, colorN, ROBIN16} from '@wandb/weave/common/util/colors';
import {
  type ClassMapsByLayer,
  type ImageType,
  nullableTaggableStrip,
  type Type,
} from '@wandb/weave/core';
import * as _ from 'lodash';
import {useMemo} from 'react';

export interface OverlayControls {
  [overlayID: string]: OverlayState;
}

export interface OverlayClassState {
  opacity: number;
  disabled: boolean;
}

export interface BaseOverlayState {
  type: ControlType;
  name: string;
  disabled: boolean;
  classSearch: string;
  classSetID: string;
  classOverlayStates: {[classID: string]: OverlayClassState};
  hideLabels?: boolean;
}

export interface BoxControlState extends BaseOverlayState {
  type: 'box';
  lineStyle?: LineStyle;
}

export interface MaskControlState extends BaseOverlayState {
  type: 'mask';
  hideImage?: boolean;
}

export interface BoxSliderState {
  [valueName: string]: BoundingBoxSliderControl;
}

export type OverlayState = BoxControlState | MaskControlState;
export type ControlType = OverlayState['type'];

export type UpdateControl = <T extends BaseOverlayState>(
  newControl: Partial<T>
) => void;

export interface ClassState {
  color: string;
  name: string;
}

export interface ClassSetState {
  classes: {
    [classID: string]: ClassState;
  };
}

export interface ClassSetControls {
  [classSetID: string]: ClassSetState;
}

function createBaseControls(
  name: string,
  classSetID: string,
  classSet: ClassSetState
): Omit<BaseOverlayState, 'type'> {
  const classStates = Object.fromEntries(
    Object.keys(classSet.classes).map(classID => [
      classID,
      {opacity: 1, disabled: false},
    ])
  );
  return {
    name,
    classSetID,
    classOverlayStates: classStates,
    classSearch: '',
    disabled: false,
  };
}

export function createMaskControls(
  ...args: Parameters<typeof createBaseControls>
): MaskControlState {
  return {...createBaseControls(...args), type: 'mask', hideImage: false};
}

export function createBoxControls(
  ...args: Parameters<typeof createBaseControls>
): BoxControlState {
  return {
    ...createBaseControls(...args),
    type: 'box',
    lineStyle: 'line',
  };
}

const defaultClassSetID = 'default';

function shouldReuseSavedControl(
  savedControl: OverlayState | undefined,
  expectedType: ControlType,
  currentClassSetID: string,
  layerClassSetID: string,
  hasLayerClassMap: boolean,
  classSets: ClassSetControls
) {
  const savedClassSetID = savedControl?.classSetID;
  if (savedControl?.type !== expectedType || savedClassSetID == null) {
    return false;
  }
  // New-style data: saved controls must match the current per-layer class set.
  if (savedClassSetID === currentClassSetID) {
    return true;
  }
  if (hasLayerClassMap) {
    return false;
  }
  // Legacy data: allow saved controls that point at the default or layer class set.
  return (
    (savedClassSetID === defaultClassSetID ||
      savedClassSetID === layerClassSetID) &&
    savedClassSetID in classSets
  );
}

export type MaskClassLabels = {
  key: string;
  type: string;
  value: {[key: string]: string};
};

const toClassValue = (className: string, classId: string) => {
  const idNOrNan = parseInt(classId, 10);
  const color = isNaN(idNOrNan)
    ? colorFromName(classId)
    : colorN(idNOrNan, ROBIN16);
  return {color, name: className};
};

export const useImageControls = (
  inputType: Type,
  currentControls?: OverlayControls,
  maskClassLabels?: {[key: string]: MaskClassLabels}
) => {
  const usableType = useMemo(() => {
    return nullableTaggableStrip(inputType) as ImageType;
  }, [inputType]);

  const classSets = useMemo(() => {
    // Legacy broken system where we have a single dict where each box and mask
    // clobbers the classes from the previous one, e.g. if box A has `1: foo`,
    // and box B has `1: bar`, only `1: bar` will be retained, and it will be
    // used for both boxes A and B.
    //
    // The preferred path is now to read from boxClassMaps and maskClassMaps
    // further below, but we keep this support for backward compatibility with
    // data written by old SDKs.
    const defaultClassSet = _.mapValues(
      usableType.classMap ?? {},
      (className, classId) => toClassValue(className, classId)
    ) as ClassSetState['classes'];

    // Legacy attempt to use mask class sets from the run config, but those are
    // actually only set if you happened to also log the same masks directly
    // as run metrics and now try to use them from a table.
    const maskClassSetsFromRunConfig = Object.entries(
      maskClassLabels ?? {}
    ).reduce((acc, [maskKey, mask]) => {
      const controlId = `mask-${maskKey.replace('image_wandb_delimeter_', '')}`;
      acc[controlId] = {
        classes: _.mapValues(mask.value, (className, classId) =>
          toClassValue(className, classId)
        ),
      };
      return acc;
    }, {} as ClassSetControls);

    // Current best practice fields, written to by the latest SDK.
    const buildClassSetsFromTableColumnTypes = (
      classMapsByLayer: ClassMapsByLayer | undefined,
      prefix: 'mask-' | 'box-'
    ): ClassSetControls =>
      _.fromPairs(
        Object.entries(classMapsByLayer ?? {}).map(([layerName, classMap]) => [
          `${prefix}${layerName}`,
          {
            classes: _.mapValues(classMap, (className, classId) =>
              toClassValue(className, classId)
            ),
          },
        ])
      );
    const boxClassSetsFromTableColumnTypes = buildClassSetsFromTableColumnTypes(
      usableType.boxClassMaps,
      'box-'
    );
    const maskClassSetsFromTableColumnTypes =
      buildClassSetsFromTableColumnTypes(usableType.maskClassMaps, 'mask-');

    // Fallback to extract labels from the layers fields.
    let defaultBoxSets: ClassSetControls = {};
    if (_.isEmpty(boxClassSetsFromTableColumnTypes)) {
      const boxLayers = usableType.boxLayers ?? {};
      defaultBoxSets = _.fromPairs(
        _.keys(boxLayers).map(boxId => [
          `box-${boxId}`,
          {classes: defaultClassSet},
        ])
      );
    }
    let defaultMaskSets: ClassSetControls = {};
    if (
      _.isEmpty(maskClassSetsFromTableColumnTypes) &&
      _.isEmpty(maskClassSetsFromRunConfig)
    ) {
      const maskLayers = usableType.maskLayers ?? {};
      defaultMaskSets = _.fromPairs(
        _.keys(maskLayers).map(maskId => [
          `mask-${maskId}`,
          {classes: defaultClassSet},
        ])
      );
    }

    // Table-schema sources take precedence over the (likely dead)
    // run-config fallback in `classSetsFromLabels`.
    return {
      [defaultClassSetID]: {classes: defaultClassSet},
      ...defaultBoxSets,
      ...defaultMaskSets,
      ...maskClassSetsFromRunConfig,
      ...boxClassSetsFromTableColumnTypes,
      ...maskClassSetsFromTableColumnTypes,
    } as ClassSetControls;
  }, [usableType, maskClassLabels]);

  const maskControls: {[key: string]: MaskControlState} = useMemo(() => {
    const maskLayers = usableType.maskLayers ?? {};
    return _.fromPairs(
      _.keys(maskLayers).map(maskId => {
        const prefixedId = 'mask-' + maskId;
        let classSetId = defaultClassSetID;
        if (prefixedId in classSets) {
          classSetId = prefixedId;
        }
        const currentControl = currentControls?.[prefixedId];
        if (
          shouldReuseSavedControl(
            currentControl,
            'mask',
            classSetId,
            prefixedId,
            maskId in (usableType.maskClassMaps ?? {}),
            classSets
          )
        ) {
          return [prefixedId, currentControl as MaskControlState];
        }
        const classSet = classSets[classSetId];
        const classSubset = _.pick(classSet.classes, ...maskLayers[maskId]);
        const newControl: MaskControlState = createMaskControls(
          prefixedId,
          classSetId,
          {classes: classSubset}
        );
        return [prefixedId, newControl];
      })
    );
  }, [usableType, classSets, currentControls]);

  const boxControls: {[key: string]: BoxControlState} = useMemo(() => {
    const boxLayers = usableType.boxLayers ?? {};
    return _.fromPairs(
      _.keys(boxLayers).map(boxId => {
        const prefixedId = 'box-' + boxId;
        let classSetId = defaultClassSetID;
        if (prefixedId in classSets) {
          classSetId = prefixedId;
        }
        const currentControl = currentControls?.[prefixedId];
        if (
          shouldReuseSavedControl(
            currentControl,
            'box',
            classSetId,
            prefixedId,
            boxId in (usableType.boxClassMaps ?? {}),
            classSets
          )
        ) {
          return [prefixedId, currentControl as BoxControlState];
        }
        const classSet = classSets[classSetId];
        const classSubset = _.pick(classSet.classes, ...boxLayers[boxId]);
        const newControl: BoxControlState = createBoxControls(
          prefixedId,
          classSetId,
          {
            classes: classSubset,
          }
        );
        return [prefixedId, newControl];
      })
    );
  }, [usableType, classSets, currentControls]);

  const controls = useMemo(() => {
    return {
      ...maskControls,
      ...boxControls,
    };
  }, [maskControls, boxControls]);

  return {
    maskControls,
    boxControls,
    controls,
    classSets,
  };
};
